'use client';

import { Feature, Permission } from '@prisma/client';
import { getSettingsNavItems } from '~/components/settings/getSettingsNavItems';
import useAbility from '~/hooks/useAbility';
import { useWorkspace } from '~/contexts/WorkspaceContext';
import { api } from '~/utils/api';

export function useSettingsNavItems() {
  const { workspaceId } = useWorkspace();
  const { isLoading, data } = api.user.authorize.useQuery();
  const ability = useAbility();

  const isBrandAllowed = data?.user.tenant.multiBrandSupport ?? false;
  const isAdmin =
    data?.user.tenantRole === 'TENANT_ADMIN' || !!data?.user.UserWorkspaces[0]?.Role.isAdmin;
  const isEnvironmentAllowed = false;
  const isEnterprise = data?.user.tenant.enabledFeatures?.includes(Feature.ENTERPRISE) ?? false;
  const canEditFileAccess = !!ability?.can('do', Permission.EDIT_FILE_ACCESS);
  const canViewSharedLinks =
    data?.user.tenant.enabledFeatures?.includes(Feature.SHAREABLE_LINKS) ?? false;
  const canViewInfluencerMonitoring =
    (data?.user.tenant.enabledFeatures?.includes(Feature.INFLUENCER_MONITORING) ?? false) &&
    !!ability?.can('do', Permission.INFLUENCER_MONITORING);
  const canViewCommunicationReview =
    (data?.user.tenant.enabledFeatures?.includes(Feature.COMMUNICATION_REVIEW) ?? false) &&
    !!ability?.can('do', Permission.VIEW_COMMUNICATION_REVIEW);
  const canViewReviewQueues =
    (data?.user.tenant.enabledFeatures?.includes(Feature.MARKETING_TASKS) ?? false) &&
    !!ability?.can('do', Permission.VIEW_MARKETING_TASK);
  const canViewClaimsApproval =
    data?.user.tenant.enabledFeatures?.includes(Feature.CLAIMS_APPROVAL) ?? false;
  const canViewRemediation =
    (data?.user.tenant.enabledFeatures?.includes(Feature.REMEDIATION) ?? false) &&
    !!ability?.can('do', Permission.REMEDIATION_QUEUE);

  const items = workspaceId
    ? getSettingsNavItems({
        canEditBrands: isBrandAllowed,
        canSyncEnvironments: isEnvironmentAllowed,
        canEditUsers: isAdmin,
        canEditFileAccess,
        canViewAuditLogs: isAdmin,
        canViewReviewQueues,
        canViewClaimsApproval,
        canViewRemediation,
        canViewSharedLinks,
        canViewInfluencerMonitoring,
        canViewCommunicationReview,
        isEnterprise,
        workspaceId,
      })
    : [];

  return { items, isLoading: isLoading || !workspaceId, workspaceId };
}
